/*******************************************************************************
 * Copyright     : Copyright (c)  2026 Honeywell Inc. All rights reserved.
 *                 This work contains trade secrets and confidential materials
 *                 of Honeywell Inc.No part may be reproduced or transmitted in any form by any
 *                 means or for any purpose without the express written permission of Honeywell. *
 *******************************************************************************/

import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  IAvailableSoftwareVersion,
  ISoftwareVersionDetails,
  ISoftwareVersionDownloadSasUrl,
} from "../../models/softwaredetails";
import {
  useGetSoftwareVersionDetailsQuery,
  useGetSoftwareVersionDownloadSasUrlQuery,
  useSyncCloudInstallMutation,
} from "../../services/api";
import { downloadLargeFile } from "../../services/file-service-api/fileDownloader";
import SoftwareDetails from "../EBICognition/SoftwareDetails";
import SoftwareDialog from "../SoftwareDialog";
import { formatFileSize } from "../../utils/utility";
import {
  SoftwareDialogTableHeader,
  SoftwareDialogTableRow,
} from "../../models/softwareDialog";
import { LOCALE_CONST } from "../../utils/constants";
import { useTranslation } from "react-i18next";
import { ContentLoader, ToastContext } from "@forge/common";
import { getErrorInfo } from "../../reusablecomponents/errorInfo/getErrorInfo";
import { NetworkErrObj } from "../gateway-report/utils/gatewayTypes";

interface SoftwareProps {
  selectedAvailableSoftwareVersions: IAvailableSoftwareVersion[];
  installedSoftwareVersion?: string;
  gatewayType?: string;
  deviceId?: string;
  connectivity?: string;
  isLoading?: boolean;
}

const Software: React.FC<SoftwareProps> = ({
  selectedAvailableSoftwareVersions,
  installedSoftwareVersion,
  gatewayType,
  deviceId,
  connectivity,
  isLoading,
}) => {
  const { t } = useTranslation();
  const [selectedVersion, setSelectedVersion] = useState<{
    version: string;
    releaseType: string;
  }>({ version: "", releaseType: "" });
  const [fileToDownload, setFileToDownload] = useState<{
    version: string;
    releaseType: string;
  }>({ version: "", releaseType: "" });
  const [selectedSoftwareVersionDetails, setSelectedSoftwareVersionDetails] =
    useState<ISoftwareVersionDetails>();
  const [selectedSoftwareDownloadDetails, setSelectedSoftwareDownloadDetails] =
    useState<ISoftwareVersionDownloadSasUrl>();
  const [isSoftwareDialogOpen, setIsSoftwareDialogOpen] = useState(false);
  const [softwareDialogMode, setSoftwareDialogMode] = useState<
    "availableVersionDetails" | "details"
  >("details");
  const [showToast, setShowToast] = useState(false);
  const [toastType, setToastType] = useState<
    "error" | "success" | "warning" | "progress"
  >("success");
  const [toastContent, setToastContent] = useState("");
  const convertFn = (value: string) => t(value);
  const { addError, addSuccess, removeAll } = useContext(ToastContext);
  const TIMEOUT_DURATION = 3000;
  const [syncCloudInstall] = useSyncCloudInstallMutation();

  const {
    data: softwareVersionDetails,
    error: softwareVersionDetailsError,
    isLoading: softwareVersionDetailsLoading,
  } = useGetSoftwareVersionDetailsQuery(
    {
      gatewayType: gatewayType,
      version: selectedVersion.version,
      releaseType: selectedVersion.releaseType,
    },
    {
      skip:
        !gatewayType ||
        !selectedVersion.version ||
        !selectedVersion.releaseType,
    },
  );

  const {
    data: softwareVersionDownloadSasUrl,
    error: softwareVersionDownloadSasUrlError,
    isLoading: softwareVersionDownloadSasUrlLoading,
  } = useGetSoftwareVersionDownloadSasUrlQuery(
    {
      gatewayType: gatewayType,
      version: fileToDownload.version,
      releaseType: fileToDownload.releaseType,
    },
    {
      skip:
        !gatewayType || !fileToDownload.version || !fileToDownload.releaseType,
    },
  );

  // Clear state when parameters change
  useEffect(() => {
    setSelectedSoftwareVersionDetails(undefined);
  }, [selectedVersion.version, selectedVersion.releaseType]);

  useEffect(() => {
    // Handle errors first to prevent showing cached data from previous successful calls
    if (softwareVersionDetailsError) {
      setSelectedSoftwareVersionDetails(undefined);
    } else if (softwareVersionDetails !== undefined && !softwareVersionDetailsLoading) {
      setSelectedSoftwareVersionDetails(softwareVersionDetails);
    }
  }, [softwareVersionDetails, softwareVersionDetailsError, softwareVersionDetailsLoading]);

  useEffect(() => {
    if (softwareVersionDownloadSasUrl) {
      setSelectedSoftwareDownloadDetails(softwareVersionDownloadSasUrl);
    } else if (softwareVersionDownloadSasUrlError) {
      displayErrorToast(
        (softwareVersionDownloadSasUrlError as NetworkErrObj)?.status,
        getErrorObjValue(softwareVersionDownloadSasUrlError as NetworkErrObj),
        false,
      );
      setFileToDownload({ version: "", releaseType: "" });
    }
  }, [softwareVersionDownloadSasUrl, softwareVersionDownloadSasUrlError]);

  useEffect(() => {
    if (selectedSoftwareDownloadDetails) {
      const performDownload = async () => {
        const { error } = await downloadLargeFile(
          selectedSoftwareDownloadDetails.downloadUrl,
          selectedSoftwareDownloadDetails.fileName || fileToDownload.version,
        );

        if (error) {
          showErrorToast(error);
        }
      };

      performDownload();
    }
    setFileToDownload({ version: "", releaseType: "" });
  }, [selectedSoftwareDownloadDetails]);

  useEffect(() => {
    if (showToast) {
      const timer = setTimeout(() => {
        setShowToast(false);
        removeAll();
      }, TIMEOUT_DURATION);

      return () => clearTimeout(timer);
    }
  }, [showToast, toastType]);

  const displayErrorToast = (
    statusCode: number,
    errMsg?: string,
    fetchConfig?: boolean,
  ) => {
    const errObj = getErrorInfo(statusCode, convertFn);
    if (!fetchConfig) {
      showErrorToast(errMsg || errObj.description);
    } else {
      showErrorToast(t(LOCALE_CONST.noRespReceivedSystem));
    }
  };

  const getErrorObjValue = (error: NetworkErrObj) => {
    if (!error) {
      return "";
    } else if (typeof error.data === "object") {
      return error.data?.Message || "";
    } else if (typeof error.data === "string") {
      return error.data || "";
    } else {
      return "";
    }
  };

  const showErrorToast = (message: string) => {
    setToastType("error");
    setToastContent(message);
    setShowToast(true);
  };

  const showSuccessToast = (message: string) => {
    setToastType("success");
    setToastContent(message);
    setShowToast(true);
  };

  const handleSaveChanges = async (version: string) => {
    if (!deviceId || !version) {
      return;
    }

    try {
      await syncCloudInstall({
        deviceId,
        version,
        status: "installed",
      }).unwrap();
      showSuccessToast(t(LOCALE_CONST.softwareVersionUpdated));
    } catch (error) {
      displayErrorToast(
        (error as NetworkErrObj)?.status,
        getErrorObjValue(error as NetworkErrObj),
        false,
      );
    }
  };

  useEffect(() => {
    if (showToast) {
      if (toastType === "error") {
        addError({
          id: "uop-toast-message",
          className: "uop-toast-message",
          title: "",
          subtitle: "",
          showClose: true,
          showDismiss: false,
          content: toastContent,
        });
      } else if (toastType === "success") {
        addSuccess({
          id: "uop-toast-message",
          className: "uop-toast-message",
          title: "",
          subtitle: "",
          showClose: true,
          showDismiss: false,
          content: toastContent,
        });
      }
    }
  }, [showToast, toastType, toastContent, addError, addSuccess]);

  // Transform software version details into table format for SoftwareDialog
  const softwareDialogTableHeaders = useMemo<SoftwareDialogTableHeader[]>(
    () => [
      { id: "component", content: t(LOCALE_CONST.component) },
      { id: "version", content: t(LOCALE_CONST.version) },
    ],
    [t],
  );

  const softwareDialogTableData = useMemo<SoftwareDialogTableRow[]>(() => {
    if (!selectedSoftwareVersionDetails?.components) return [];
    return selectedSoftwareVersionDetails.components.map((item, index) => ({
      id: `row-${index}`,
      cell: [{ content: item.component }, { content: item.version }],
    }));
  }, [selectedSoftwareVersionDetails]);

  return (
    <>
      <div className="mt-4">
        {isLoading && <ContentLoader />}
        {!isLoading && (
          <SoftwareDetails
            availableSoftwareVersions={selectedAvailableSoftwareVersions}
            installedSoftwareVersion={installedSoftwareVersion}
            isCreate={false}
            isDisabled={connectivity === "Connected"}
            showDownload={true}
            isConnected={connectivity === "Connected"}
            onDownload={(version, releaseType) => {
              setFileToDownload({ version, releaseType });
            }}
            onViewDetails={(version, releaseType) => {
              setSelectedVersion({ version, releaseType });
              setSoftwareDialogMode("details");
              setIsSoftwareDialogOpen(true);
            }}
            onSaveChanges={(version) => {
              handleSaveChanges(version);
            }}
            onVersionCardClick={(version, releaseType) => {
              setSelectedVersion({ version, releaseType });
              setSoftwareDialogMode("availableVersionDetails");
              setIsSoftwareDialogOpen(true);
            }}
          />
        )}
      </div>

      <SoftwareDialog
        isOpen={isSoftwareDialogOpen}
        onClose={() => setIsSoftwareDialogOpen(false)}
        softwareVersion={selectedVersion.version}
        downloadSize={formatFileSize(
          selectedSoftwareVersionDetails?.size || "0",
        )}
        tableHeaders={softwareDialogTableHeaders}
        tableData={softwareDialogTableData}
        showFooterText={softwareDialogMode === "availableVersionDetails"}
        showDownload={false}
      />
    </>
  );
};

export default Software;
